import { ADMIN_PREFERENCE_CACHE_KEYS } from '@/shared/constants/cache-keys'

import type { AdminLocaleDefinition } from './locale-definition'

interface AdminLocaleModule {
  default: AdminLocaleDefinition
}

export interface AdminLocaleStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

export const DEFAULT_ADMIN_LOCALE = 'zh-CN'

const localeModules = import.meta.glob<AdminLocaleModule>('./locales/*.ts', { eager: true })

export const adminLocales = Object.freeze(
  Object.values(localeModules)
    .map(module => module.default)
    .sort((left, right) => left.order - right.order)
)

const localeByCode = new Map(adminLocales.map(locale => [locale.code, locale]))

if (!localeByCode.has(DEFAULT_ADMIN_LOCALE)) {
  throw new Error(`Admin 默认语言资源缺失：${DEFAULT_ADMIN_LOCALE}`)
}
if (localeByCode.size !== adminLocales.length) {
  throw new Error('Admin 语言资源 code 不能重复')
}

const browserStorage = (): AdminLocaleStorage | undefined => {
  return typeof localStorage === 'undefined' ? undefined : localStorage
}

export const resolveAdminLocale = (value: string | null | undefined): string => {
  return value && localeByCode.has(value) ? value : DEFAULT_ADMIN_LOCALE
}

export const readAdminLocale = (storage = browserStorage()): string => {
  try {
    return resolveAdminLocale(storage?.getItem(ADMIN_PREFERENCE_CACHE_KEYS.locale))
  } catch {
    return DEFAULT_ADMIN_LOCALE
  }
}

export const writeAdminLocale = (
  locale: string,
  storage = browserStorage()
): string => {
  const resolved = resolveAdminLocale(locale)
  storage?.setItem(ADMIN_PREFERENCE_CACHE_KEYS.locale, resolved)
  return resolved
}

export const getAdminLocale = (
  locale: string | null | undefined
): AdminLocaleDefinition => localeByCode.get(resolveAdminLocale(locale))!

export const resolveAdminApiLocale = (storage = browserStorage()): string => {
  return readAdminLocale(storage)
}
